// ─────────────────────────────────────────────────────────────────────────────
// THE BROKER'S COUNTER-SIGNATURE QUEUE.
//
// 🔑 THE PENDING SET IS THE POINT. A broker with thirty agents needs to know who
// is waiting on them, not to read a roster and work it out. So pending renders
// first, at full weight, and the counter-signed ones collapse underneath.
//
// ⚠️ THE TWO SETS COME FROM THE SERVER ALREADY SEPARATED, and are not partitioned
// here. Splitting one array client-side is how a count and a list start
// disagreeing — the same reason the Accounts KPIs derive from the array the table
// renders rather than a second fetch.
//
// 🚨 AND NOTHING HERE IS BLOCKING ANYBODY, WHICH THE SCREEN SAYS OUT LOUD. An
// agent whose agreement is sitting in this queue can still work, submit buyers
// and be owed — held, not forfeited. A broker who believes their agents are
// stuck will chase this at the expense of things that are actually blocked, and
// a queue that looks urgent without being urgent trains them to ignore it.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' };

export default function FirmAgreements({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [signing, setSigning] = useState(null);   // { partner_id, name }
  const [typedName, setTypedName] = useState('');
  const [doc, setDoc] = useState(null);
  const [docError, setDocError] = useState('');

  const load = useCallback(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/doorlypro/firm/agreements`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      // ⚠️ A 404 IS THE ROLE GUARD, NOT A FAILURE. requireBroker 404s so the view
      // is not confirmed to exist; a non-broker simply renders nothing.
      .catch(() => setData({ pending: [], signed: [], unavailable: true }));
  }, [token]);

  useEffect(load, [load]);

  if (!data || data.unavailable) return null;
  const pending = data.pending || [];
  const signed = data.signed || [];
  if (!pending.length && !signed.length) return null;

  // 🚨 OPEN THE DOCUMENT, THEN SIGN. The control asked a broker to type their name
  // beside an agent's name and nothing else — no text, no version, no date, no
  // sight of what they were binding the firm to. Their signature is what makes the
  // firm a party to it, so the document is fetched and shown first.
  // 🔑 VIEW IS THE SAME MODAL WITHOUT THE PEN. A counter-signed agreement is the
  // one a broker is most likely to need again — a colleague asks what they signed,
  // or a version mismatch on the row above raises a question — and the row showed
  // two dates with no way to open the document behind them.
  //
  // ⚠️ readOnly IS CARRIED ON THE TARGET, NOT IN A SECOND MODAL. Two modals over
  // one document is how the signing one ends up rendering a document the viewing
  // one has already changed.
  const openFor = async (r, readOnly = false) => {
    setSigning({ ...r, readOnly }); setTypedName(''); setError(''); setDoc(null); setDocError('');
    try {
      const res = await axios.get(
        `${API_URL}/api/doorlypro/firm/agreements/${r.partner_id}/document`,
        { headers: { Authorization: `Bearer ${token}` } });
      setDoc(res.data);
    } catch (e) {
      // ⚠️ THE SIGNATURE IS STILL OFFERED. An agreement signed before the PDF was
      // retained has no document, and refusing to let the broker sign would hold
      // their agent's fee over a missing file rather than a missing decision —
      // the modal says plainly that there is nothing to display.
      setDocError(e.response?.data?.error || 'Could not load the document.');
    }
  };

  const submit = async () => {
    if (!typedName.trim()) { setError('Type your name to sign.'); return; }
    setBusyId(signing.partner_id); setError('');
    try {
      await axios.post(
        `${API_URL}/api/doorlypro/firm/agreements/${signing.partner_id}/countersign`,
        { signed_name: typedName.trim() },
        { headers: { Authorization: `Bearer ${token}` } });
      setSigning(null); setTypedName(''); setDoc(null); setDocError('');
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not record that signature.');
    } finally { setBusyId(null); }
  };

  const fullName = (r) => `${r.first_name || ''} ${r.last_name || ''}`.trim();

  return (
    <div style={{ marginBottom: 24 }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: '#111827' }}>Your team’s agreements</h2>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280' }}>
        As the broker of record, yours is the counter-signature. It’s also what lets us copy you if
        one of your agents becomes unreachable to a buyer.
      </p>

      {error && (
        <div style={{ marginBottom: 12, padding: '10px 14px', background: '#fff0f3', color: '#ff082f', fontSize: 13, borderRadius: 8 }}>{error}</div>
      )}

      {/* ── the queue ──────────────────────────────────────────────────── */}
      {pending.length > 0 && (
        <div style={{ ...card, marginBottom: 14 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
              Waiting for your signature ({pending.length})
            </span>
          </div>
          {pending.map((r, i) => (
            <div key={r.partner_id} style={{ borderBottom: i < pending.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
              <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{fullName(r)}</div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>
                    Signed {r.agreement_signed_at ? new Date(r.agreement_signed_at).toLocaleDateString() : '—'}
                    {r.agreement_version ? ` · version ${r.agreement_version}` : ''}
                  </div>
                </div>
                <button
                  onClick={() => openFor({ partner_id: r.partner_id, name: fullName(r) })}
                  disabled={busyId === r.partner_id}
                  style={{ padding: '8px 16px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  Counter-sign
                </button>
              </div>

            </div>
          ))}
          {/* ── 🚨 REVIEW, THEN SIGN ─────────────────────────────────────────
              The signature is what binds the FIRM, so the document it binds them
              to is on screen while they give it. */}
          {signing && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
              <div onClick={() => { setSigning(null); setError(''); setDoc(null); }}
                style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />
              <div style={{ position: 'relative', background: '#fff', borderRadius: 14, width: '100%', maxWidth: 860, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>
                    {doc?.title || 'Partner Agreement'} — {signing.name}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>
                    {doc?.signed_name ? `Signed by ${doc.signed_name}` : 'Signed'}
                    {doc?.signed_at ? ` on ${new Date(doc.signed_at).toLocaleDateString()}` : ''}
                    {doc?.version ? ` · version ${doc.version}` : ''}
                  </div>
                </div>

                <div style={{ flex: 1, overflow: 'auto', background: '#f9fafb', minHeight: 240 }}>
                  {doc?.pdf_url ? (
                    <iframe title="Agreement" src={doc.pdf_url}
                      style={{ width: '100%', height: '55vh', border: 'none', display: 'block' }} />
                  ) : (
                    <div style={{ padding: 28, textAlign: 'center', fontSize: 13, color: '#6b7280', lineHeight: 1.7 }}>
                      {docError
                        ? docError
                        : doc
                        ? 'This agreement was signed before Doorly began keeping a copy of the document, so there is nothing to display here. The signature details above are on record, and your counter-signature still applies to the version named.'
                        : 'Loading the agreement…'}
                    </div>
                  )}
                </div>

                <div style={{ padding: '14px 20px', borderTop: '1px solid #f3f4f6' }}>
                  {error && (
                    <div style={{ marginBottom: 10, fontSize: 13, color: '#ff082f' }}>{error}</div>
                  )}
                  {/* ⚠️ NO SIGNATURE FIELD ON A DOCUMENT ALREADY COUNTER-SIGNED.
                      The endpoint is idempotent and the first signature wins, so a
                      second attempt would change nothing — but offering the field
                      invites a broker to perform an act that has no effect and
                      then wonder which of the two dates is real. */}
                  {signing.readOnly ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12.5, color: '#6b7280' }}>
                        Counter-signed{doc?.countersigned_at ? ` on ${new Date(doc.countersigned_at).toLocaleDateString()}` : ''}.
                      </span>
                      <button onClick={() => { setSigning(null); setDoc(null); }}
                        style={{ padding: '9px 16px', background: '#111827', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        Close
                      </button>
                    </div>
                  ) : (<>
                  {/* ⚠️ TYPED, NOT A CONFIRM BUTTON. This is a signature: the name
                      is the act, and it is stored as countersigned_name beside its
                      own date. A one-click confirm would record a signature
                      nobody performed. */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      value={typedName} onChange={(e) => setTypedName(e.target.value)}
                      placeholder="Type your full name to counter-sign"
                      style={{ flex: 1, minWidth: 220, padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
                    />
                    <button onClick={submit} disabled={busyId === signing.partner_id}
                      style={{ padding: '10px 18px', background: '#111827', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                      {busyId === signing.partner_id ? 'Signing…' : 'Counter-sign'}
                    </button>
                    <button onClick={() => { setSigning(null); setError(''); setDoc(null); }}
                      style={{ padding: '10px 12px', background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                  </>)}
                </div>
              </div>
            </div>
          )}

          {/* 🚨 SAID ON THE SCREEN, NOT ONLY IN THE EMAIL. */}
          <div style={{ padding: '10px 16px', background: '#f9fafb', fontSize: 12, color: '#6b7280' }}>
            Nothing here is blocking your agents — they can keep working and submitting buyers while
            these wait.
          </div>
        </div>
      )}

      {/* ── already counter-signed ─────────────────────────────────────── */}
      {signed.length > 0 && (
        <details style={card}>
          <summary style={{ padding: '12px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#111827' }}>
            Counter-signed ({signed.length})
          </summary>
          {signed.map((r, i) => (
            <div key={r.partner_id} style={{ padding: '11px 16px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, flexWrap: 'wrap' }}>
              <span style={{ color: '#111827', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                {fullName(r)}
                <button onClick={() => openFor({ partner_id: r.partner_id, name: fullName(r) }, true)}
                  style={{ padding: '3px 10px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 12, fontWeight: 700, color: '#374151', cursor: 'pointer' }}>
                  View
                </button>
              </span>
              <span style={{ color: '#9ca3af' }}>
                {/* ⚠️ BOTH DATES, BECAUSE THERE ARE TWO SIGNATURES. Showing only
                    one implies a single act by a single person. */}
                agent {r.agreement_signed_at ? new Date(r.agreement_signed_at).toLocaleDateString() : '—'}
                {' · '}you {r.countersigned_at ? new Date(r.countersigned_at).toLocaleDateString() : '—'}
                {/* 🚨 AND A VERSION MISMATCH IS SHOWN RATHER THAN HIDDEN. If the
                    instrument bumped between the two signatures they are against
                    different texts, and that is a fact somebody may need. */}
                {r.countersigned_version && r.agreement_version
                  && r.countersigned_version !== r.agreement_version
                  ? ` · versions differ (${r.agreement_version} / ${r.countersigned_version})`
                  : ''}
              </span>
            </div>
          ))}
        </details>
      )}
    </div>
  );
}
